import { useCallback, useEffect, useState } from "react";
import { type Coords, reverseGeocode } from "@/lib/geo";

type Status = "idle" | "prompt" | "loading" | "granted" | "denied" | "unavailable" | "error";

interface State {
  status: Status;
  coords: Coords | null;
  city: string | null;
  error: string | null;
}

const CACHE_KEY = "fig.geo.cache";
const TTL_MS = 1000 * 60 * 30; // 30 min

interface CacheShape { coords: Coords; city: string | null; ts: number }

function readCache(): CacheShape | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheShape;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed;
  } catch { return null; }
}

function writeCache(c: CacheShape) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch { /* quota */ }
}

/**
 * Browser geolocation with a friendly state machine + city reverse-geocode.
 * - `request()` asks the browser permission prompt
 * - Caches last fix for 30 min so the Nearby tab is instant on revisit
 * - Returns a `city` label from OSM (free, no key) for human-readable UI
 */
export function useGeolocation(autoStart = false) {
  const [state, setState] = useState<State>(() => {
    const cached = readCache();
    if (cached) {
      return { status: "granted", coords: cached.coords, city: cached.city, error: null };
    }
    return { status: "idle", coords: null, city: null, error: null };
  });

  const request = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setState({ status: "unavailable", coords: null, city: null, error: "Your browser doesn't support location." });
      return;
    }
    setState((s) => ({ ...s, status: "loading", error: null }));

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords: Coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        // Optimistically show coords; reverse-geocode in background.
        setState({ status: "granted", coords, city: null, error: null });
        const city = await reverseGeocode(coords);
        setState({ status: "granted", coords, city, error: null });
        writeCache({ coords, city, ts: Date.now() });
      },
      (err) => {
        const denied = err.code === err.PERMISSION_DENIED;
        setState({
          status: denied ? "denied" : "error",
          coords: null,
          city: null,
          error: denied
            ? "Location blocked. Enable it in your browser settings to see nearby finds."
            : err.message || "Couldn't get your location.",
        });
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 1000 * 60 * 5 }
    );
  }, []);

  const clear = useCallback(() => {
    try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
    setState({ status: "idle", coords: null, city: null, error: null });
  }, []);

  // Optional: probe permission state without prompting.
  useEffect(() => {
    if (!autoStart || state.coords) return;
    if (typeof navigator === "undefined" || !("permissions" in navigator)) return;
    navigator.permissions
      ?.query({ name: "geolocation" as PermissionName })
      .then((p) => {
        if (p.state === "granted") request();
        else if (p.state === "denied") setState((s) => ({ ...s, status: "denied" }));
        else setState((s) => ({ ...s, status: "prompt" }));
      })
      .catch(() => { /* older browsers */ });
  }, [autoStart, request, state.coords]);

  return { ...state, request, clear };
}
